/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Row, Col } from "react-awesome-styled-grid";
import { Link } from "react-router-dom";
import { Cell } from "react-table";
import { Box, Text } from "@nordcloud/gnui";
import { FlexContainer, showSuccess, Table } from "~/components";
import { EnvironmentAdd } from "~/components/Applications/ApplicationDetails/EnvironmentAdd";
import { useShowError } from "~/components/Error/useShowError";
import { Application, ApplicationEnv, EnvironmentCreateInput } from "~/models";
import { ROUTES } from "~/routing";
import { generateActionSuccessText, useAxios } from "~/tools";
import { displayEnumValue } from "~/tools/display/displayEnumValue";

type Props = {
  application: Application;
  refetch: () => Promise<void>;
};

function Environments({ application, refetch }: Props) {
  const { Axios } = useAxios();
  const { showError } = useShowError();

  const addEnvironment = React.useCallback(
    async (newEnv: EnvironmentCreateInput) => {
      try {
        await Axios().post("/environments", {
          customerId: application.customer.id,
          applicationId: application.id,
          ...newEnv,
        });
        showSuccess(
          generateActionSuccessText(`Environment ${newEnv.name}`)()("added")()
        );
      } catch (err) {
        showError(err.response);
      }

      return refetch();
    },
    [Axios, application, showError, refetch]
  );

  const columns = React.useMemo(
    () => [
      {
        Header: "Environment",
        accessor: "name",
        Cell: ({ row, value }: Cell<ApplicationEnv>) => (
          <Link
            to={`${ROUTES.customers.index}/${application.customer.id}/${application.id}/${row.original.id}`}
          >
            {value}
          </Link>
        ),
      },
      {
        Header: "Type",
        accessor: "type",
        Cell: ({ value }: Cell<ApplicationEnv>) => displayEnumValue(value),
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }: Cell<ApplicationEnv>) => displayEnumValue(value),
      },
      {
        Header: "Service Level",
        accessor: "attachedServiceLevel.serviceLevel.name",
        Cell: ({ value }: Cell<ApplicationEnv>) =>
          displayEnumValue(value) || "-",
      },
    ],
    [application]
  );

  const employees = [...application.employees];
  if (application.owner) {
    employees.unshift(application.owner);
  }

  return (
    <Box spacing="spacing04">
      <Row>
        <Col lg={12}>
          <FlexContainer>
            <Text weight="medium">Environments</Text>
            <EnvironmentAdd
              employees={employees}
              addEnvironment={addEnvironment}
            />
          </FlexContainer>
          <Table columns={columns} data={application.environments} mb="0" />
        </Col>
      </Row>
    </Box>
  );
}

export { Environments };
